import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, Signal, WritableSignal, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { SettingsService } from '@app/_services/settings.service';
import { ModalService } from '@app/_services/modal.service';
import { NotificationComponent } from '@app/components/common/notifications/notification/notification.component';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { MeUserAvatarComponent } from '@app/components/common/account/me-user-avatar/me-user-avatar.component';
import { CollaboratorService, CollaboratorSummary, SharedEntityItem } from '@app/_services/collaborator.service';
import { BlockedUserService } from '@app/_services/blocked-user.service';
import { PublicNameService } from '@app/_services/public-name.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { Subject, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { EntityMemberResolverService } from '@app/_services/entity-member-resolver.service';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';

@Component({
    selector: 'app-collaborators-page',
    imports: [
        CommonModule,
        SvgComponent,
        NotificationComponent,
        NoResultBlockComponent,
        MeUserAvatarComponent,
        ReactiveFormsModule,
        MatMenuModule,
    ],
    templateUrl: './collaborators-page.component.html',
    styleUrl: './collaborators-page.component.scss'
})
export class CollaboratorsPageComponent implements OnInit, OnDestroy {
    private readonly modalService = inject(ModalService);
    private readonly collaboratorService = inject(CollaboratorService);
    private readonly blockedUserService = inject(BlockedUserService);
    private readonly publicNameService = inject(PublicNameService);
    private readonly as = inject(AnalyticsService);
    readonly ss = inject(SettingsService);
    private readonly dialog = inject(MatDialog);
    private readonly resolver = inject(EntityMemberResolverService);
    private readonly alertService = inject(AlertService);
    public readonly cc = inject(CacheService);
    private readonly router = inject(Router);

    @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

    // Email input form control for mat-menu
    emailControl = new FormControl('', [Validators.required, Validators.email]);

    // Loading state for add collaborator action
    isLoading: WritableSignal<boolean> = signal(false);

    collaborators: Signal<CollaboratorSummary[]> = computed(() => {
        // Establish reactive dependencies on all entity stores via resolver
        this.resolver.getAllEntityArrays();

        return this.collaboratorService.getAllCollaborators();
    });

    /**
     * Get display name for a collaborator.
     * Service handles ALL fallback logic internally!
     * Matches Flutter behavior: never shows email as title.
     */
    getDisplayName(collaborator: CollaboratorSummary): string {
        // Service returns: cached name → email → "New Collaborator"
        return this.publicNameService.getDisplayName(collaborator.hashedEmail);
    }

    /**
     * Check if we have a real name (not email, not "New Collaborator").
     * Used to determine whether to show name or "New Collaborator" as title.
     */
    hasDisplayName(collaborator: CollaboratorSummary): boolean {
        const name = this.publicNameService.getDisplayName(collaborator.hashedEmail);
        // Check if it's not the email and not the fallback
        return name !== collaborator.email && name !== 'New Collaborator';
    }

    /**
     * Get initial for avatar.
     * Returns first letter of name if available, or '?' if fallback.
     */
    getInitial(collaborator: CollaboratorSummary): string {
        const name = this.publicNameService.getDisplayName(collaborator.hashedEmail);
        if (name && name.trim() && name !== 'New Collaborator') {
            return name.charAt(0).toUpperCase();
        }
        return '?';
    }

    // Track selected collaborator for shared entities modal
    selectedCollaborator: WritableSignal<CollaboratorSummary | null> = signal(null);

    // Shared entities for selected collaborator
    selectedCollaboratorEntities: Signal<SharedEntityItem[]> = computed(() => {
        const collab = this.selectedCollaborator();
        if (!collab) return [];
        return this.collaboratorService.getEntitiesSharedWith(collab.email);
    });

    // Analytics
    screenEnterTime: Signal<Date> = signal(new Date());
    unSubscribe = new Subject<void>();

    ngOnInit() {
        this.analyticsInit();
        this.syncCollaboratorEmails();
        this.preloadCollaboratorNames();
    }

    navigateToToday() {
        this.router.navigate(['/today']);
    }

    /**
     * Preload display names for all collaborators using PublicNameService.
     * Follows Flutter pattern: async preload in initState, synchronous display in build.
     */
    private async preloadCollaboratorNames(): Promise<void> {
        const collaborators = this.collaborators();

        // Extract all hashed emails for preloading
        const hashedEmails = collaborators.map(c => c.hashedEmail);

        // Service handles all the fetching, caching, and fallback logic!
        await this.publicNameService.preload(hashedEmails);
    }

    /**
     * Sync collaborator emails from entities to userResources.collaboratorEmails
     * This ensures autocomplete suggestions are populated from existing shared entities
     */
    private async syncCollaboratorEmails(): Promise<void> {
        // Get emails from all shared entities
        const emailsFromEntities = this.collaboratorService.getAllCollaboratorEmailsFromEntities();

        // Get existing suggestions
        const existingSuggestions = new Set(
            this.blockedUserService.getCollaboratorSuggestions().map(e => e.toLowerCase())
        );

        // Find emails that are in entities but not in suggestions
        const missingEmails = Array.from(emailsFromEntities)
            .filter(email => !existingSuggestions.has(email));

        // Add missing emails to userResources
        for (const email of missingEmails) {
            await this.blockedUserService.cacheCollaboratorEmail(email);
        }
    }

    ngOnDestroy() {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }

    private analyticsInit() {
        this.as.screenViewEvent('collaborators', 'none' as AnalyticsScreen);
    }

    /**
     * Open shared entities modal for a collaborator
     */
    async openSharedEntitiesModal(collaborator: CollaboratorSummary) {
        this.selectedCollaborator.set(collaborator);

        // Lazy load the SharedEntitiesModalComponent
        const { SharedEntitiesModalComponent } = await import('../shared-entities-modal/shared-entities-modal.component');

        this.modalService.openLevel2(SharedEntitiesModalComponent,
            {
                collaborator,
                entities: this.collaboratorService.getEntitiesSharedWith(collaborator.email)
            },
            { autoFocus: false }
        );
    }

    /**
     * Open blocked users modal
     */
    async openBlockedUsersModal() {
        // Lazy load the BlockedUsersModalComponent
        const { BlockedUsersModalComponent } = await import('../blocked-users-modal/blocked-users-modal.component');

        this.modalService.openLevel2(BlockedUsersModalComponent);
    }

    /**
     * Open add collaborator modal
     */
    async openAddCollaboratorModal() {
        // Lazy load the AddCollaboratorModalComponent
        const { AddCollaboratorModalComponent } = await import('../add-collaborator-modal/add-collaborator-modal.component');

        const dialogRef = this.modalService.openLevel3(AddCollaboratorModalComponent, { topbar: true });

        this.modalService.getClosedObservable(dialogRef).pipe(take(1)).subscribe(async (result: { email?: string; addedToSuggestions?: boolean } | undefined) => {
            if (result?.email && result.addedToSuggestions) {
                // Preload the display name using the service (pass raw email)
                await this.publicNameService.preload(result.email);
            }
        });
    }

    /**
     * Add email to collaborator suggestions from mat-menu
     */
    async addToSuggestions() {
        const email = this.emailControl.value?.trim().toLowerCase();

        if (!email) {
            this.alertService.alert(
                this.ss.t('common_error'),
                this.ss.t('collaborators_enterEmail'),
            );
            return;
        }

        if (!this.isValidEmail(email)) {
            this.alertService.alert(
                this.ss.t('common_error'),
                this.ss.t('collaborators_invalidEmail'),
            );
            return;
        }

        // Check if user is trying to add themselves
        if (email === this.cc.user?.email?.toLowerCase()) {
            this.alertService.alert(
                this.ss.t('common_error'),
                this.ss.t('collaborators_cantAddSelf'),
            );
            return;
        }

        // Check if email already exists in suggestions
        const existingSuggestions = this.blockedUserService.getCollaboratorSuggestions()
            .map(e => e.toLowerCase());
        if (existingSuggestions.includes(email)) {
            this.alertService.alert(
                this.ss.t('memberAlreadyExists_title'),
                this.ss.t('memberAlreadyExists_content'),
            );
            return;
        }

        try {
            this.isLoading.set(true);

            // Cache the email for future suggestions
            await this.blockedUserService.cacheCollaboratorEmail(email);

            // Preload the display name using the service (pass raw email)
            await this.publicNameService.preload(email);

            // Clear the input and close the menu
            this.emailControl.reset();
            this.menuTrigger?.closeMenu();

        } catch (error) {
            console.error('Failed to add to suggestions:', error);
            this.alertService.alert(
                'Error',
                'Failed to save email. Please try again.',
            );
        } finally {
            this.isLoading.set(false);
        }
    }

    /**
     * Validate email format
     */
    private isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

}
